import * as React from 'react';
import { FormProvider, useFormContext, Controller, useForm, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '../../lib/utils';

// Wrapper component to provide context
export function Form({ children, ...props }: React.FormHTMLAttributes<HTMLFormElement>) {
  return <form {...props}>{children}</form>;
}

// Generic form root (helper to create provider outside if desired)
export function RHFProvider({ methods, onSubmit, children }:{ methods: any; onSubmit: (data: any) => void; children: React.ReactNode; }) {
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
}

interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {}
export const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('space-y-1', className)} {...props} />
  )
);
FormItem.displayName = 'FormItem';

export const FormLabel = ({ className, children, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('text-sm font-medium', className)} {...props}>{children}</label>
);

export const FormDescription = ({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-xs text-muted-foreground', className)} {...props}>{children}</p>
);

export const FormMessage = ({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => {
  if (!children) return null;
  return <p className={cn('text-xs font-medium text-red-600', className)} {...props}>{children}</p>;
};

interface FormFieldProps {
  name: string;
  control?: any;
  render: (args: { field: any; fieldState: any; formState: any }) => React.ReactElement;
}

export function FormField({ name, control, render }: FormFieldProps) {
  const ctx = useFormContext();
  const usedControl = control || ctx.control;
  return (
    <Controller
      name={name}
      control={usedControl}
      render={({ field, fieldState, formState }) => render({ field, fieldState, formState })}
    />
  );
}

// Utility to create methods with zod schema
export function useZodForm<TFieldValues extends FieldValues>({ schema, defaultValues }:{ schema: any; defaultValues?: Partial<TFieldValues>; }) {
  return useForm<TFieldValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any,
    mode: 'onSubmit'
  });
}
